import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  client,
  databases,
  DATABASE_ID,
  USERS_COLLECTION_ID,
  EVENTS_COLLECTION_ID,
  User,
} from '../config/appwrite';

/**
 * Real-time session monitoring hook using Appwrite subscription
 *
 * Subscribes to user document changes and automatically logs out if:
 * - User is blocked
 * - Session token doesn't match (logged in elsewhere)
 * - User document is deleted
 */
const useRealtimeSession = (userId: string | undefined) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;

    const sessionToken = sessionStorage.getItem('sessionToken');
    if (!sessionToken) {
      navigate('/login');
      return;
    }

    console.log('🔔 Setting up real-time session monitoring for user:', userId);

    // Subscribe to this specific user's document changes
    const unsubscribeUser = client.subscribe(
      `databases.${DATABASE_ID}.collections.${USERS_COLLECTION_ID}.documents.${userId}`,
      (response: any) => {
        console.log('👤 User document changed:', response);

        // Handle different event types
        if (
          response.events.includes(
            `databases.${DATABASE_ID}.collections.${USERS_COLLECTION_ID}.documents.${userId}.delete`
          )
        ) {
          console.log('❌ User document deleted, logging out');
          sessionStorage.removeItem('sessionToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          localStorage.removeItem('activeEventId');
          navigate('/login');
          return;
        }

        if (
          response.events.includes(
            `databases.${DATABASE_ID}.collections.${USERS_COLLECTION_ID}.documents.${userId}.update`
          )
        ) {
          const userData = response.payload as User;

          // Check if user is blocked
          if (userData.is_blocked === true) {
            console.log('🚫 User has been blocked, logging out');
            sessionStorage.removeItem('sessionToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            localStorage.removeItem('activeEventId');
            navigate('/login');
            return;
          }

          // Check if session token changed (logged in elsewhere)
          if (userData.last_logout_tab !== sessionToken) {
            console.log('🔄 Session changed in another tab/device, logging out');
            sessionStorage.removeItem('sessionToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            localStorage.removeItem('activeEventId');
            navigate('/login');
            return;
          }

          // Update local storage with latest user data
          localStorage.setItem(
            'user',
            JSON.stringify({
              id: userData.id || userId,
              username: userData.username,
              role: userData.role,
              is_blocked: userData.is_blocked,
              is_logged_in: userData.is_logged_in,
              last_logout_tab: userData.last_logout_tab,
              name: userData.name,
              jamiaat: userData.jamiaat,
              password: userData.password,
              created_at: userData.created_at,
              last_active: userData.last_active,
            })
          );
        }
      }
    );

    // Subscribe to events collection changes (for active event updates)
    const unsubscribeEvents = client.subscribe(
      `databases.${DATABASE_ID}.collections.${EVENTS_COLLECTION_ID}.documents`,
      async (response: any) => {
        console.log('📅 Events collection changed:', response);

        // Check if there's an active event
        try {
          const activeEvents = await databases.listDocuments(DATABASE_ID, EVENTS_COLLECTION_ID, []);

          const activeEvent = activeEvents.documents.find((doc: any) => doc.is_active === true);

          if (activeEvent) {
            const currentActiveEventId = localStorage.getItem('activeEventId');
            if (currentActiveEventId !== activeEvent.$id) {
              console.log('🔄 Active event changed, updating...');
              localStorage.setItem('activeEventId', activeEvent.$id);
              sessionStorage.setItem('eventUpdate', 'true');
            }
          } else {
            // No active event
            if (localStorage.getItem('activeEventId')) {
              console.log('❌ No active event, clearing...');
              localStorage.removeItem('activeEventId');
              sessionStorage.setItem('eventUpdate', 'true');
            }
          }
        } catch (error) {
          console.error('Error checking active events:', error);
        }
      }
    );

    // Cleanup subscriptions on unmount
    return () => {
      console.log('🔕 Cleaning up real-time subscriptions');
      unsubscribeUser();
      unsubscribeEvents();
    };
  }, [userId, navigate]);
};

export default useRealtimeSession;
